import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/Layout';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { ArrowLeft, Save, Plus, X, Link as LinkIcon, Instagram, Youtube, Globe } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

export default function AdminVideoEdit() {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    goal_outcome: '',
    order_index: 1,
    watch_url: '',
    embed_url: '',
    resource_links_json: [],
    tiktok_url: '',
    instagram_url: '',
    youtube_url: '',
    website_url: '',
    published: true
  });
  const [newResourceLink, setNewResourceLink] = useState('');

  useEffect(() => {
    fetchVideo();
  }, [videoId]);

  const fetchVideo = async () => {
    try {
      const response = await axios.get(`${API}/videos/${videoId}`);
      setVideo(response.data);
      setFormData({
        title: response.data.title || '',
        goal_outcome: response.data.goal_outcome || '',
        order_index: response.data.order_index || 1,
        watch_url: response.data.watch_url || '',
        embed_url: response.data.embed_url || '',
        resource_links_json: response.data.resource_links_json || [],
        tiktok_url: response.data.tiktok_url || '',
        instagram_url: response.data.instagram_url || '',
        youtube_url: response.data.youtube_url || '',
        website_url: response.data.website_url || '',
        published: response.data.published ?? true
      });

      // Fetch series info
      const seriesRes = await axios.get(`${API}/series/${response.data.series_id}`);
      setSeries(seriesRes.data);
    } catch (error) {
      console.error('Error fetching video:', error);
      toast.error('Failed to load video');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.title || !formData.watch_url) {
      toast.error('Title and Watch URL are required');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/videos/${videoId}`, formData);
      toast.success('Video saved successfully!');
      navigate(`/admin/series/${video.series_id}`);
    } catch (error) {
      toast.error('Failed to save video');
    } finally {
      setSaving(false);
    }
  };

  const addResourceLink = () => {
    if (newResourceLink && formData.resource_links_json.length < 5) {
      setFormData({
        ...formData,
        resource_links_json: [...formData.resource_links_json, newResourceLink]
      });
      setNewResourceLink('');
    }
  };

  const removeResourceLink = (index) => {
    setFormData({
      ...formData,
      resource_links_json: formData.resource_links_json.filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-edupaws-sage border-t-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Back Button */}
      <button
        onClick={() => navigate(`/admin/series/${video?.series_id}`)}
        className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6 font-body"
        data-testid="back-to-series"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to {series?.title}
      </button>

      <div className="max-w-3xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <span className="inline-block bg-edupaws-sage/20 text-edupaws-navy px-3 py-1 rounded-full text-sm font-body font-medium mb-2">
              Video {formData.order_index}
            </span>
            <h1 className="font-heading text-2xl font-bold text-gray-800">
              Edit Video
            </h1>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-video-btn"
            className="flex items-center gap-2 font-body font-medium px-6 py-2.5 bg-edupaws-navy text-white rounded-lg hover:bg-navy-700 transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Video'}
          </button>
        </div>

        {/* Form */}
        <div className="space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm space-y-4">
            <h2 className="font-heading text-lg font-bold text-gray-800 mb-4">
              Basic Information
            </h2>

            <div className="space-y-2">
              <Label className="font-body font-medium text-gray-700">
                Video Title *
              </Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Welcome & Getting Started"
                data-testid="video-title-input"
                className="h-11 rounded-lg"
              />
            </div>

            <div className="space-y-2">
              <Label className="font-body font-medium text-gray-700">
                Goal / Outcome *
              </Label>
              <Textarea
                value={formData.goal_outcome}
                onChange={(e) => setFormData({ ...formData, goal_outcome: e.target.value })}
                placeholder="What will the learner achieve after watching this video?"
                data-testid="video-goal-input"
                className="rounded-lg resize-none"
                rows={3}
              />
            </div>

            <div className="flex items-center justify-between py-2">
              <div>
                <span className="font-body font-medium text-gray-700 block">Published</span>
                <span className="text-sm text-gray-500 font-body">Make this video visible to owners</span>
              </div>
              <Switch
                checked={formData.published}
                onCheckedChange={(checked) => setFormData({ ...formData, published: checked })}
                data-testid="video-published-switch"
              />
            </div>
          </div>

          {/* Video URLs */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm space-y-4">
            <h2 className="font-heading text-lg font-bold text-gray-800 mb-4">
              Video URLs
            </h2>

            <div className="space-y-2">
              <Label className="font-body font-medium text-gray-700">
                Watch URL (Vimeo) *
              </Label>
              <Input
                value={formData.watch_url}
                onChange={(e) => setFormData({ ...formData, watch_url: e.target.value })}
                placeholder="https://vimeo.com/..."
                data-testid="video-watch-url-input"
                className="h-11 rounded-lg"
              />
              <p className="text-xs text-gray-500 font-body">
                The main video URL that opens in a new tab
              </p>
            </div>

            <div className="space-y-2">
              <Label className="font-body font-medium text-gray-700">
                Embed URL (optional)
              </Label>
              <Input
                value={formData.embed_url}
                onChange={(e) => setFormData({ ...formData, embed_url: e.target.value })}
                placeholder="https://player.vimeo.com/video/..."
                data-testid="video-embed-url-input"
                className="h-11 rounded-lg"
              />
              <p className="text-xs text-gray-500 font-body">
                Used for the embedded preview player
              </p>
            </div>
          </div>

          {/* Resource Links */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm space-y-4">
            <h2 className="font-heading text-lg font-bold text-gray-800 mb-4">
              Resource Links
            </h2>
            <p className="text-sm text-gray-500 font-body mb-4">
              Add up to 5 resource links (PDFs, worksheets, etc.)
            </p>

            {formData.resource_links_json.map((link, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <LinkIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <span className="flex-1 text-sm text-gray-600 font-body truncate">{link}</span>
                <button
                  onClick={() => removeResourceLink(idx)}
                  className="p-1 hover:bg-red-50 rounded text-red-500"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}

            {formData.resource_links_json.length < 5 && (
              <div className="flex gap-2">
                <Input
                  value={newResourceLink}
                  onChange={(e) => setNewResourceLink(e.target.value)}
                  placeholder="https://..."
                  data-testid="new-resource-link-input"
                  className="h-10 rounded-lg"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addResourceLink())}
                />
                <button
                  onClick={addResourceLink}
                  data-testid="add-resource-link-btn"
                  className="px-4 h-10 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* Social Links */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm space-y-4">
            <h2 className="font-heading text-lg font-bold text-gray-800 mb-4">
              Social Links
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-body font-medium text-gray-700 flex items-center gap-2">
                  <TikTokIcon className="w-4 h-4" /> TikTok
                </Label>
                <Input
                  value={formData.tiktok_url}
                  onChange={(e) => setFormData({ ...formData, tiktok_url: e.target.value })}
                  placeholder="https://tiktok.com/..."
                  data-testid="video-tiktok-input"
                  className="h-10 rounded-lg"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-body font-medium text-gray-700 flex items-center gap-2">
                  <Instagram className="w-4 h-4" /> Instagram
                </Label>
                <Input
                  value={formData.instagram_url}
                  onChange={(e) => setFormData({ ...formData, instagram_url: e.target.value })}
                  placeholder="https://instagram.com/..."
                  data-testid="video-instagram-input"
                  className="h-10 rounded-lg"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-body font-medium text-gray-700 flex items-center gap-2">
                  <Youtube className="w-4 h-4" /> YouTube
                </Label>
                <Input
                  value={formData.youtube_url}
                  onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                  placeholder="https://youtube.com/..."
                  data-testid="video-youtube-input"
                  className="h-10 rounded-lg"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-body font-medium text-gray-700 flex items-center gap-2">
                  <Globe className="w-4 h-4" /> Website
                </Label>
                <Input
                  value={formData.website_url}
                  onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                  placeholder="https://..."
                  data-testid="video-website-input"
                  className="h-10 rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
